/**
 * Pure state reducers for live relay mode (task-11).
 *
 * The daemon pushes protocol payloads (`approval.request`, `session.tail`,
 * `exec.*`, `chat.*`) through the E2E relay. These reducers validate/merge them
 * into the local view models deterministically so the wiring in AppState stays
 * thin and everything is unit-testable without React or a socket.
 */
import type {
  ApprovalStatusItem,
  ExecOutput,
  ExecResult,
  ExecStarted,
  SessionTail,
} from '@luminis/protocol';
import {
  approvalRequestFromUnknown,
  approvalViewFromRequest,
  sessionEventFromWire,
  sessionViewFromSummary,
} from '../api/client';
import { nowSeconds } from '../id';
import { mergeSessionEvents, sessionKey } from '../sessions';
import type { ApprovalView, SessionEventView, SessionView } from '../types';

export const LIVE_APPROVAL_LIMIT = 50;
export const LIVE_EXEC_LIMIT = 20;
/** Per-stream output cap shown in the UI (older output rolls off). */
export const MAX_EXEC_OUTPUT_CHARS = 6000;
export const MAX_EXEC_OUTPUT_LINES = 120;
const MAX_EXEC_SEEN_SEQ = 500;

// ---------------------------------------------------------------------------
// Small guards (live payloads are still validated before they reach the UI)
// ---------------------------------------------------------------------------

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function asFiniteNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

// ---------------------------------------------------------------------------
// Approvals
// ---------------------------------------------------------------------------

/**
 * Map a decrypted `approval.request` payload into an ApprovalView.
 * Invalid payloads are dropped; an already-expired request stays visible but is
 * marked `expired` so the UI cannot act on it.
 */
export function approvalViewFromPayload(payload: unknown, now = nowSeconds()): ApprovalView | null {
  const request = approvalRequestFromUnknown(payload);
  if (!request) return null;
  const view = approvalViewFromRequest(request, now);
  return request.expires_at <= now ? { ...view, status: 'expired' } : view;
}

/**
 * Insert/update an incoming approval request. Existing entries keep their local
 * decision/delivery state; duplicates are ignored (the relay can replay
 * envelopes after a reconnect).
 */
export function upsertIncomingApproval(
  approvals: readonly ApprovalView[],
  view: ApprovalView,
  limit = LIVE_APPROVAL_LIMIT,
): ApprovalView[] {
  if (approvals.some((item) => item.approval_id === view.approval_id)) return [...approvals];
  return [view, ...approvals].slice(0, limit);
}

export type ApprovalDeliveryState = 'sending' | 'sent' | 'failed';

/** Reflect the relay delivery result for a locally sent decision. */
export function markApprovalDelivery(
  approvals: readonly ApprovalView[],
  approvalId: string,
  state: ApprovalDeliveryState,
  error?: string,
): ApprovalView[] {
  return approvals.map((approval) => {
    if (approval.approval_id !== approvalId) return approval;
    return {
      ...approval,
      delivery_state: state,
      ...(state === 'failed'
        ? { decision_error: error ?? '审批决定发送失败' }
        : { decision_error: undefined }),
    };
  });
}

// ---------------------------------------------------------------------------
// Sessions
// ---------------------------------------------------------------------------

function mergeSessionView(existing: SessionView | undefined, incoming: SessionView): SessionView {
  if (!existing) return incoming;
  const sameTitle = incoming.title !== incoming.session_id;
  return {
    ...existing,
    ...(sameTitle ? { title: incoming.title } : {}),
    status: incoming.status,
    last_event_at: Math.max(existing.last_event_at, incoming.last_event_at),
    ...(incoming.tokens !== undefined ? { tokens: incoming.tokens } : {}),
    ...(incoming.cost_cny !== undefined ? { cost_cny: incoming.cost_cny } : {}),
    ...(incoming.started_at !== undefined ? { started_at: incoming.started_at } : {}),
    ...(incoming.progress !== undefined ? { progress: incoming.progress } : {}),
    ...(incoming.phase !== undefined ? { phase: incoming.phase } : {}),
    ...(incoming.model !== undefined ? { model: incoming.model } : {}),
    ...(incoming.parent_session_id !== undefined
      ? { parent_session_id: incoming.parent_session_id }
      : {}),
    ...(incoming.control !== undefined ? { control: incoming.control } : {}),
    events: existing.events,
  };
}

/** Merge an inbound `session.list.response` payload (unknown JSON) into the list. */
export function mergeIncomingSessionList(
  sessions: readonly SessionView[],
  payload: unknown,
  now = nowSeconds(),
): SessionView[] {
  if (!isRecord(payload) || !Array.isArray(payload.sessions)) return [...sessions];
  const byKey = new Map(sessions.map((session) => [sessionKey(session), session]));
  for (const raw of payload.sessions) {
    if (!isRecord(raw)) continue;
    const mapped = sessionViewFromSummary(raw as never, now);
    if (!mapped) continue;
    const key = sessionKey(mapped);
    byKey.set(key, mergeSessionView(byKey.get(key), mapped));
  }
  return [...byKey.values()];
}

/**
 * Merge an inbound `session.tail` payload into the list. Unknown sessions are
 * created as active entries (the list request may still be in flight); known
 * sessions get their event buffer extended with de-duplication and ordering.
 *
 * Duplicates (relay replay after reconnect/resync) are dropped by
 * `mergeSessionEvents`; timestamps are kept in seconds and `last_event_at` is
 * clamped to `now` so a connector lying about the future cannot break the
 * duration/recency display. `next_cursor` is remembered for diagnostics only —
 * the protocol has no phone→daemon tail-continuation request frame.
 */
export function mergeIncomingSessionTail(
  sessions: readonly SessionView[],
  payload: unknown,
  now = nowSeconds(),
): SessionView[] {
  const tail = parseSessionTail(payload);
  if (!tail) return [...sessions];
  const events = tail.events;
  if (events.length === 0) {
    if (tail.next_cursor === undefined) return [...sessions];
  }
  const mergedEvents = mergeSessionEvents([], events);
  const latest = mergedEvents.reduce((max, event) => Math.max(max, event.ts), 0);
  const index = sessions.findIndex(
    (session) => session.source === tail.source && session.session_id === tail.session_id,
  );
  if (index < 0) {
    if (mergedEvents.length === 0) return [...sessions];
    const created: SessionView = {
      source: tail.source,
      session_id: tail.session_id,
      title: tail.session_id,
      status: 'active',
      started_at: mergedEvents[0]?.ts,
      last_event_at: Math.min(latest, now),
      events: mergedEvents,
      ...(tail.next_cursor !== undefined ? { cursor: tail.next_cursor } : {}),
    };
    return [...sessions, created];
  }
  const existing = sessions[index];
  if (!existing) return [...sessions];
  const merged: SessionView = {
    ...existing,
    events:
      mergedEvents.length > 0 ? mergeSessionEvents(existing.events, mergedEvents) : existing.events,
    last_event_at: Math.max(existing.last_event_at, Math.min(latest, now)),
    ...(tail.next_cursor !== undefined ? { cursor: tail.next_cursor } : {}),
  };
  const next = [...sessions];
  next[index] = merged;
  return next;
}

export interface ParsedSessionTail {
  source: SessionTail['source'];
  session_id: string;
  events: SessionEventView[];
  next_cursor?: string;
}

const SESSION_SOURCES = new Set(['dsh', 'codex', 'opencode', 'vscode', 'luminis']);

export function parseSessionTail(payload: unknown): ParsedSessionTail | null {
  if (!isRecord(payload)) return null;
  const source = asString(payload.source);
  const sessionId = asString(payload.session_id);
  if (!source || !SESSION_SOURCES.has(source) || !sessionId) return null;
  const rawEvents = Array.isArray(payload.events) ? payload.events : [];
  const events = rawEvents
    .map((event) => sessionEventFromWire(event))
    .filter((event): event is SessionEventView => event !== null);
  return {
    source: source as SessionTail['source'],
    session_id: sessionId,
    events,
    ...(typeof payload.next_cursor === 'string' ? { next_cursor: payload.next_cursor } : {}),
  };
}

// ---------------------------------------------------------------------------
// Execution (exec.started / exec.output / exec.result)
// ---------------------------------------------------------------------------

export type ExecStatus = 'running' | 'ok' | 'failed';

export interface ExecView {
  exec_id: string;
  tool: string;
  pid?: number;
  status: ExecStatus;
  started_at: number;
  updated_at: number;
  exit_code?: number | null;
  duration_ms?: number;
  stdout: string;
  stderr: string;
  /** Output sequence numbers already appended (dedupe for replays). */
  output_seqs: number[];
  truncated: boolean;
}

function appendOutput(text: string, chunk: string): { text: string; truncated: boolean } {
  let next = text + chunk;
  let truncated = false;
  if (next.length > MAX_EXEC_OUTPUT_CHARS) {
    next = `…${next.slice(-(MAX_EXEC_OUTPUT_CHARS - 1))}`;
    truncated = true;
  }
  const lines = next.split('\n');
  if (lines.length > MAX_EXEC_OUTPUT_LINES) {
    next = lines.slice(-MAX_EXEC_OUTPUT_LINES).join('\n');
    truncated = true;
  }
  return { text: next, truncated };
}

function upsertExec(state: readonly ExecView[], view: ExecView): ExecView[] {
  const index = state.findIndex((item) => item.exec_id === view.exec_id);
  if (index < 0) return [view, ...state].slice(0, LIVE_EXEC_LIMIT);
  const next = [...state];
  next[index] = view;
  return next;
}

function getExec(state: readonly ExecView[], execId: string): ExecView | undefined {
  return state.find((item) => item.exec_id === execId);
}

function parseExecStarted(payload: unknown): ExecStarted | null {
  if (!isRecord(payload)) return null;
  const execId = asString(payload.exec_id);
  const tool = asString(payload.tool);
  if (!execId || !tool) return null;
  return {
    exec_id: execId,
    tool,
    ...(asFiniteNumber(payload.pid) !== undefined ? { pid: asFiniteNumber(payload.pid) } : {}),
  };
}

function parseExecOutput(payload: unknown): ExecOutput | null {
  if (!isRecord(payload)) return null;
  const execId = asString(payload.exec_id);
  const seq = asFiniteNumber(payload.seq);
  const data = asString(payload.data);
  const stream = payload.stream;
  if (!execId || seq === undefined || !Number.isInteger(seq) || data === undefined) return null;
  if (stream !== 'stdout' && stream !== 'stderr') return null;
  return { exec_id: execId, seq, stream, data };
}

function parseExecResult(payload: unknown): ExecResult | null {
  if (!isRecord(payload)) return null;
  const execId = asString(payload.exec_id);
  if (!execId) return null;
  return {
    exec_id: execId,
    exit_code: asFiniteNumber(payload.exit_code) ?? null,
    ok: payload.ok === true,
    ...(asFiniteNumber(payload.duration_ms) !== undefined
      ? { duration_ms: asFiniteNumber(payload.duration_ms) }
      : {}),
  };
}

export interface ExecReducerResult {
  state: ExecView[];
  changed: ExecView | null;
}

/**
 * Apply one exec event. Returns the same shape for unknown/invalid payloads so
 * the caller can skip React state updates (reference equality preserved).
 */
export function applyExecEvent(
  state: readonly ExecView[],
  type: string,
  payload: unknown,
  now = nowSeconds(),
): ExecReducerResult {
  if (type === 'exec.started') {
    const parsed = parseExecStarted(payload);
    if (!parsed) return { state: [...state], changed: null };
    const existing = getExec(state, parsed.exec_id);
    const view: ExecView = {
      ...(existing ?? {
        exec_id: parsed.exec_id,
        tool: parsed.tool,
        status: 'running' as ExecStatus,
        started_at: now,
        stdout: '',
        stderr: '',
        output_seqs: [],
        truncated: false,
      }),
      tool: parsed.tool,
      ...(parsed.pid !== undefined ? { pid: parsed.pid } : {}),
      updated_at: now,
    };
    return { state: upsertExec(state, view), changed: view };
  }
  if (type === 'exec.output') {
    const parsed = parseExecOutput(payload);
    if (!parsed) return { state: [...state], changed: null };
    const existing: ExecView = getExec(state, parsed.exec_id) ?? {
      exec_id: parsed.exec_id,
      tool: '(未命名执行)',
      status: 'running' as ExecStatus,
      started_at: now,
      updated_at: now,
      stdout: '',
      stderr: '',
      output_seqs: [],
      truncated: false,
    };
    if (existing.output_seqs.includes(parsed.seq)) return { state: [...state], changed: null };
    const appended = appendOutput(
      parsed.stream === 'stderr' ? existing.stderr : existing.stdout,
      parsed.data,
    );
    const view: ExecView = {
      ...existing,
      ...(parsed.stream === 'stderr' ? { stderr: appended.text } : { stdout: appended.text }),
      output_seqs: [...existing.output_seqs, parsed.seq].slice(-MAX_EXEC_SEEN_SEQ),
      truncated: existing.truncated || appended.truncated,
      updated_at: now,
    };
    return { state: upsertExec(state, view), changed: view };
  }
  if (type === 'exec.result') {
    const parsed = parseExecResult(payload);
    if (!parsed) return { state: [...state], changed: null };
    const existing: ExecView = getExec(state, parsed.exec_id) ?? {
      exec_id: parsed.exec_id,
      tool: '(未命名执行)',
      status: 'running' as ExecStatus,
      started_at: now,
      updated_at: now,
      stdout: '',
      stderr: '',
      output_seqs: [],
      truncated: false,
    };
    const view: ExecView = {
      ...existing,
      status: parsed.ok ? 'ok' : 'failed',
      exit_code: parsed.exit_code,
      ...(parsed.duration_ms !== undefined ? { duration_ms: parsed.duration_ms } : {}),
      updated_at: now,
    };
    return { state: upsertExec(state, view), changed: view };
  }
  return { state: [...state], changed: null };
}

/**
 * Merge daemon execution state into approval cards (`exec_id === approval_id`).
 *
 * The daemon only starts executing after a valid signed decision, so a final
 * `exec.result` moves a still-pending card to `granted` and attaches the output;
 * `exec.started` keeps it pending but shows the running state.
 */
export function applyExecToApprovals(
  approvals: readonly ApprovalView[],
  execs: readonly ExecView[],
): ApprovalView[] {
  if (execs.length === 0) return [...approvals];
  const byId = new Map(execs.map((exec) => [exec.exec_id, exec]));
  let changed = false;
  const next = approvals.map((approval) => {
    const exec = byId.get(approval.approval_id);
    if (!exec) return approval;
    const status = exec.status;
    const previous = approval.execution;
    const same =
      previous !== undefined &&
      previous.status === status &&
      (previous.exit_code ?? null) === (exec.exit_code ?? null) &&
      (previous.stdout ?? '') === exec.stdout &&
      (previous.stderr ?? '') === exec.stderr;
    if (same) return approval;
    changed = true;
    return {
      ...approval,
      status:
        status === 'running'
          ? approval.status
          : approval.status === 'pending'
            ? ('granted' as const)
            : approval.status,
      execution: {
        status,
        exit_code: exec.exit_code ?? null,
        stdout: exec.stdout.slice(-4000),
        stderr: exec.stderr.slice(-2000),
      },
    };
  });
  return changed ? next : [...approvals];
}

/**
 * Merge an `approval.status.response` sync into local approval cards.
 *
 * A refreshed phone still has the cards in localStorage but may have missed the
 * daemon's exec/decide events; this restores status, decision and (when the
 * daemon still remembers it) the last execution output.
 */
export function applyApprovalStatusSync(
  approvals: readonly ApprovalView[],
  items: readonly ApprovalStatusItem[],
): ApprovalView[] {
  if (items.length === 0) return [...approvals];
  const byId = new Map(items.map((item) => [item.approval_id, item]));
  let changed = false;
  const next = approvals.map((approval) => {
    const item = byId.get(approval.approval_id);
    if (!item) return approval;
    const execution =
      item.execution !== undefined
        ? {
            status: (item.execution.ok ? 'ok' : 'failed') as 'ok' | 'failed',
            exit_code: item.execution.exit_code ?? null,
            ...(item.execution.stdout !== undefined ? { stdout: item.execution.stdout } : {}),
            ...(item.execution.stderr !== undefined ? { stderr: item.execution.stderr } : {}),
          }
        : item.status === 'denied' || item.status === 'expired' || item.status === 'revoked'
          ? undefined
          : approval.execution;
    const sameStatus = approval.status === item.status;
    const sameDecision = (approval.decision ?? undefined) === (item.decision ?? undefined);
    const sameExec =
      JSON.stringify(approval.execution ?? null) === JSON.stringify(execution ?? null);
    if (sameStatus && sameDecision && sameExec) return approval;
    changed = true;
    return {
      ...approval,
      status: item.status,
      ...(item.decision !== undefined ? { decision: item.decision } : {}),
      ...(item.decided_at !== undefined ? { decided_at: item.decided_at } : {}),
      // Explicitly clear a stale running execution when the daemon says the
      // approval was denied/expired/revoked (there will be no result).
      ...(execution !== undefined ? { execution } : { execution: undefined }),
    };
  });
  return changed ? next : [...approvals];
}

export const EXEC_STATUS_LABELS: Record<ExecStatus, string> = {
  running: '执行中',
  ok: '成功',
  failed: '失败',
};

export const EXEC_STATUS_TONES: Record<ExecStatus, string> = {
  running: 'border-amber-400/30 bg-amber-400/10 text-amber-200',
  ok: 'border-emerald-400/30 bg-emerald-400/10 text-emerald-200',
  failed: 'border-rose-400/30 bg-rose-400/10 text-rose-200',
};
